import React, { useState } from "react";
import styled from "styled-components";
import PlanCard from "../Proposal/components/PlanCard/PlanCard";
import {
  SummaryFormCard,
  InputWrapper,
  ProposalFormSummaryWrapper,
  ProposalPageWrapper,
} from "../Proposal/ProposalForm.style";
import {
  serialize,
  useFinalSubmitMutation,
  usePaymentQuery,
  useProposalEnquiry,
  useUpdateOrderMutation,
} from "../../services/ProposalServices";
import { CircularProgress } from "@mui/material";
import { Button } from "../../components/Button/Button";
import Check from "../../components/Checkbox/Checkbox";
import Loader from "../../components/Loader/Loader";
import { FaEdit } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

/* ---------------- UI STYLES ---------------- */

const SummaryDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
`;

const RowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
`;

const Label = styled.span`
  color: #7a7a7a;
`;

const Value = styled.span`
  font-weight: 600;
  color: #222;
`;

/* ---------------- DATA CARD COMPONENT ---------------- */

const DataComp = ({ title, items }) => {
  return (
    <Card>
      <Title>{title}</Title>

      <RowWrapper>
        {items.map((item, i) => (
          <Row key={i}>
            <Label>{item.label}</Label>
            <Value>{item.value || "-"}</Value>
          </Row>
        ))}
      </RowWrapper>
    </Card>
  );
};

/* ---------------- MAIN COMPONENT ---------------- */

const ProposalSummary = () => {
  const { isLoading, vehicle, product, proposal } = useProposalEnquiry();
  const proposalData = proposal?.FORMDATA || {};
  const [checked, setChecked] = useState(false);

  const { data, isPaymentLoading, isResubmit } = usePaymentQuery();
  const { finalSubmit, isLoading: isSubmitting } = useFinalSubmitMutation();
  const { updateOrder } = useUpdateOrderMutation();

  const navigate = useNavigate();

  const isOD = product?.POLICY_TYPE === "OD";

  const onSubmit = (data) => {
    const { PG_FORM } = data;
    updateOrder();

    const { fields, method, url } = PG_FORM;

    if (method === "POST") {
      const form = document.createElement("form");

      Object.keys(fields).forEach((item) => {
        const input = document.createElement("input");
        input.name = item;
        input.value = fields[item];
        form.appendChild(input);
      });

      form.action = url;
      form.method = method;

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } else {
      let query = fields ? serialize(fields) : "";
      window.open(url + (query ? "?" + query : ""), "_self");
    }
  };

  const handleEditClick = () => {
    if (proposal?.QUOTE_ID) {
      navigate(`/proposal?stage=PF&proposal_id=${proposal._id}`);
    }
  };

  if (isLoading || isPaymentLoading) {
    return <Loader message="Loading Proposal Details..." />;
  }

  /* ---------------- VEHICLE ---------------- */

  const {
    vehicle_make,
    vehicle_model,
    vehicle_variant,
    fuel_type,
    vehicle_registration_year,
  } = vehicle || {};

  const carName = [vehicle_make, vehicle_model, vehicle_variant]
    .filter(Boolean)
    .join(" ");

  const currentYear = new Date().getFullYear();

  /* ---------------- PROPOSAL DATA ---------------- */

  const {
    owner_type,
    first_name,
    last_name,
    email,
    mobile_no,
    gender,
    is_married,
    dob,
    pan_no,
    occupation,
    nominee_name,
    nominee_age,
    nominee_relation,
    address,
    pincode,
    city_name,
    state_name,
    registration_number,
    engine_number,
    chassis_number,
    is_financed,
    prev_insurer_name,
    prev_policy_no,
    financier_name,
    is_pa,
    reg_address,
    reg_city_name,
    reg_pincode,
    reg_state_name,
    tp_prev_insurer_name,
    tp_prev_policy_no,
    tp_existing_exp_date,
  } = proposalData || {};

  return (
    <>
      {isSubmitting && <Loader message="Resubmitting Proposal" />}

      <ProposalPageWrapper>
        <PlanCard />

        <ProposalFormSummaryWrapper>
          <SummaryFormCard>
            {/* HEADER */}

            <div
              className="display-flex justify-content-between align-items-center fw-700 fs-18 mb-20"
            >
              <span>Important Details to review before payment</span>

              <button
                onClick={handleEditClick}
                className="bg-none border-none cursor-pointer fs-20"
              >
                <FaEdit />
              </button>
            </div>

            <SummaryDetails>
              {/* OWNER */}

              <DataComp
                title="Car Owner"
                items={[
                  {
                    label: "Name",
                    value: [first_name, last_name].filter(Boolean).join(" "),
                  },
                  { label: "Email", value: email },
                  { label: "Mobile", value: mobile_no },
                  { label: "PAN", value: pan_no },
                ]}
              />

              {/* PERSONAL */}

              {owner_type !== "COMP" && (
                <DataComp
                  title="Personal Details"
                  items={[
                    {
                      label: "Gender",
                      value:
                        gender === "M"
                          ? "Male"
                          : gender === "F"
                            ? "Female"
                            : "",
                    },
                    {
                      label: "Marital Status",
                      value: is_married === "Y" ? "Married" : "Single",
                    },
                    { label: "DOB", value: dob },
                    { label: "Occupation", value: occupation },
                  ]}
                />
              )}

              {/* NOMINEE */}

              {owner_type !== "COMP" && (
                <DataComp
                  title="Nominee Details"
                  items={[
                    { label: "Name", value: nominee_name },
                    { label: "Age", value: nominee_age },
                    { label: "Relation", value: nominee_relation },
                  ]}
                />
              )}

              {/* ADDRESS */}

              <DataComp
                title="Communication Address"
                items={[
                  { label: "Address", value: address },
                  { label: "City", value: city_name },
                  { label: "State", value: state_name },
                  { label: "Pincode", value: pincode },
                ]}
              />

              {/* VEHICLE */}

              <DataComp
                title="Your Car"
                items={[
                  { label: "Car", value: carName },
                  { label: "Fuel Type", value: fuel_type },
                  { label: "Registration Number", value: registration_number },
                  { label: "Engine Number", value: engine_number },
                  { label: "Chassis Number", value: chassis_number },
                ]}
              />

              {/* PREVIOUS POLICY */}

              {currentYear !== Number(vehicle_registration_year) && (
                <DataComp
                  title="Previous Policy"
                  items={[
                    { label: "Insurer", value: prev_insurer_name },
                    { label: "Policy Number", value: prev_policy_no },
                  ]}
                />
              )}

              {/* TP POLICY */}

              {isOD && (
                <DataComp
                  title="TP Policy"
                  items={[
                    { label: "Insurer", value: tp_prev_insurer_name },
                    { label: "Policy Number", value: tp_prev_policy_no },
                    { label: "End Date", value: tp_existing_exp_date },
                  ]}
                />
              )}

              {/* LOAN */}

              <DataComp
                title="Loan Provider"
                items={[
                  {
                    label: "Loan",
                    value:
                      is_financed === "N"
                        ? "Car Not on Loan"
                        : financier_name,
                  },
                ]}
              />
            </SummaryDetails>
          </SummaryFormCard>

          {/* PAYMENT */}

          <SummaryFormCard>
            <InputWrapper>
              <Check
                label="I declare that the information provided above is true."
                onClick={(e) => setChecked(e.target.checked)}
              />
            </InputWrapper>

            {checked && (
              <Button
                className="w-70 m-24-auto-0"
                primary
                onClick={(e) => {
                  e.preventDefault();
                  isResubmit ? finalSubmit() : onSubmit(data);
                }}
              >
                {isLoading ? (
                  <CircularProgress size="16px" className="color-white" />
                ) : (
                  "Proceed To Payment"
                )}
              </Button>
            )}
          </SummaryFormCard>
        </ProposalFormSummaryWrapper>
      </ProposalPageWrapper>
    </>
  );
};

export default ProposalSummary;